import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { ICard } from '../Card'
import type { CardAnimations, CoversList } from '../Card.h'

import {
  TextAligning,
  TextBox,
  TextBoxColors,
  TextBoxTypes,
} from 'atoms/TextBox'
import { useBooleanState, usePoster } from 'utils/hooks'
import { COLOR, FIXED_INDENTS } from 'vars/index'
import { CardContentContainerSC, PosterContainerSC } from '../base-card'
import { CARDS_ASPECT_RATIO, CardCorners, CardPosterSize } from '../Card.h'
import { BundleCardRootSC } from './BundleCard.styled'

export interface IBundleCard extends Pick<
  ICard,
  'lazyImage' | 'backgroundColorHovered'
> {
  title?: string
  subtitle?: string
  aspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  children?: JSX.Element
  childrenTransition?: CardAnimations
  transitionName?: CardAnimations
  corners?: CardCorners
  fontTitle?: ITextBoxProps['font']
  fontSubtitle?: ITextBoxProps['font']
  textAlignment?: ITextBoxProps['alignment']
  textColor?: ITextBoxProps['color']
  textMaxLines?: ITextBoxProps['maxLines']
  verticalGap?: FIXED_INDENTS
  poster?: CoversList
  posterUrl?: string | null
  posterHeight?: CardPosterSize
  posterWidth?: CardPosterSize
  posterEnabled?: boolean
  overlayOnHoverEnabled?: boolean
  overlayEnabled?: boolean
  purchaseTitle?: string
  rented?: boolean
  paid?: boolean
}

export const BundleCard = ({
  title = '',
  subtitle = '',
  aspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT,
  children,
  transitionName,
  corners = CardCorners.CORNERS_0,
  fontTitle = TextBoxTypes.Headline3,
  fontSubtitle = TextBoxTypes.Footnote5,
  textAlignment = TextAligning.Left,
  textColor = TextBoxColors.TEXT_ICON_PRIMARY,
  verticalGap = FIXED_INDENTS.INDENT_10,
  poster,
  posterHeight = CardPosterSize.Default,
  posterWidth = CardPosterSize.Default,
  posterEnabled = true,
  overlayOnHoverEnabled = true,
  overlayEnabled = true,
  purchaseTitle,
  posterUrl,
  lazyImage,
  rented,
  backgroundColorHovered = COLOR.SURFACE_OPACITY_DARK_70,
  paid,
}: IBundleCard): JSX.Element => {
  const [isPosterLoaded, handlePosterLoading, handlePosterError] =
    useBooleanState(true)

  const Poster = usePoster(title, poster)

  return (
    <BundleCardRootSC
      posterEnabled={posterEnabled}
      overlayOnHoverEnabled={overlayOnHoverEnabled}
      aspectRatio={aspectRatio}
      transitionName={transitionName}
      corners={corners}
      posterUrl={posterUrl}
      overlayEnabled={overlayEnabled}
      handlePosterLoading={handlePosterLoading}
      handlePosterError={handlePosterError}
      lazyImage={lazyImage}
      rented={rented}
      paid={paid}
      purchaseTitle={purchaseTitle}
      hidePriceOnHover={false}
    >
      <>
        {(!posterUrl || !isPosterLoaded) && (
          <PosterContainerSC
            overlayOnHoverEnabled={overlayOnHoverEnabled}
            posterHeight={posterHeight}
            posterWidth={posterWidth}
            gradient={COLOR.CARD_VERTICAL_OVERLAY}
            corners={corners}
            backgroundColorHovered={backgroundColorHovered}
          >
            <Poster />
          </PosterContainerSC>
        )}
        <CardContentContainerSC
          verticalAligning={'flex-end'}
          paddings={[
            FIXED_INDENTS.INDENT_60,
            FIXED_INDENTS.INDENT_60,
            FIXED_INDENTS.INDENT_40,
            FIXED_INDENTS.INDENT_60,
          ]}
          verticalGap={verticalGap}
        >
          {children ? (
            children
          ) : (
            <>
              <TextBox
                font={fontTitle}
                title={title}
                alignment={textAlignment}
                color={textColor}
                maxLines={aspectRatio > 1 ? 1 : 2}
              />
              <TextBox
                font={fontSubtitle}
                title={subtitle}
                alignment={textAlignment}
                color={textColor}
                maxLines={1}
              />
            </>
          )}
        </CardContentContainerSC>
      </>
    </BundleCardRootSC>
  )
}
